/**
 * Wire contract for the admin appeals LIST surface (AUT-119 /
 * PHASE-B-API-8):
 *
 *   - `GET /admin/appeals` → `ListAdminAppealsResultDto`
 *
 * Centralised in `@carcheck/shared` so the api + admin SPA import the same
 * source of truth — no api↔admin mirror drift.
 *
 * **Read-only.** The review/resolve ACTIONS already exist
 * (`POST /admin/appeals/:id/review` + `/resolve`, AUT-105); this surface is
 * the moderation queue + detail feeding the future `/admin/appeals` page.
 *
 * **PII discipline (architect 🟠 — AUT-119):** appeals span BOTH the filing
 * CUSTOMER and the accused INSPECTOR — the divergence from payouts
 * (inspector-only) and refunds (customer-only). BOTH `customerName` and
 * `inspectorName` carry REDACTED initials when the calling admin lacks
 * `view_pii`. The single `piiRevealed` flag tells the SPA which variant it
 * received for BOTH identities so it never re-redacts a full value or
 * renders a redacted value as copyable. The server is the sole authority on
 * redaction. The list surfaces only NAMES — never phone / email (those, if
 * ever needed, land on the detail).
 *
 * All `Date` fields are ISO-8601 strings (JSON has no `Date`); consumers
 * convert at the render boundary via `new Date(dto.createdAt)`.
 */
/**
 * Admin-facing appeal status — the THREE LIVE wire-stable values (architect
 * lock #1 — AUT-119). The Prisma `AppealStatus` enum has FIVE values
 * (`pending | under_review | approved | rejected | resolved`) but
 * `AppealsService` only ever writes `pending → under_review → resolved` —
 * `approved` / `rejected` are DEAD (never written by any code path). The
 * wire intentionally drops them so the SPA's status filter + chip rendering
 * only ever see the live set:
 *
 *   - `pending`      — filed, not yet picked up by an admin.
 *   - `under_review` — an admin started review (`startReview`).
 *   - `resolved`     — terminal; admin recorded the outcome (`resolve`).
 *
 * Single source of truth for the api's filter Zod schema too — the api
 * imports `ADMIN_APPEAL_STATUSES` rather than hand-typing the 3 values
 * (same pattern as `ADMIN_PAYOUT_STATUSES`). The mapper defensively coerces
 * any stray `approved` / `rejected` row (legacy / manual DB edit) so a dead
 * value can never reach the wire's strict 3-enum surface.
 */
export const ADMIN_APPEAL_STATUSES = ['pending', 'under_review', 'resolved'];
/**
 * Admin-facing appeal priority — the FULL 3-value `AppealPriority` enum
 * (non-null, default `normal`). Unlike status, NONE of these are dead, so
 * the filter is a safe 1:1 mirror with no null handling:
 *
 *   - `normal` — default triage band.
 *   - `high`   — elevated.
 *   - `urgent` — top of the queue.
 *
 * Single source of truth for the api's filter Zod schema.
 */
export const ADMIN_APPEAL_PRIORITIES = ['normal', 'high', 'urgent'];
/**
 * Admin-facing appeal reason — the FULL 5-value `AppealReason` enum. Mirror
 * of the Prisma enum, confirmed 1:1 against the live schema (no narrowing):
 *
 *   - `scam`          — inspector never inspected / faked the report.
 *   - `poor_quality`  — inspection done but substandard.
 *   - `incomplete`    — required checks were skipped.
 *   - `wrong_vehicle` — inspected the wrong car.
 *   - `other`         — free-text catch-all (see `description`).
 *
 * Exposed for the SPA's reason filter facet; the api does NOT currently
 * filter on reason (the queue filters on status / priority), but the wire
 * union is published so the SPA can render the reason chip without importing
 * `@prisma/client`.
 */
export const ADMIN_APPEAL_REASONS = [
    'scam',
    'poor_quality',
    'incomplete',
    'wrong_vehicle',
    'other',
];
//# sourceMappingURL=admin-appeal-list.js.map